import { forwardRef, useImperativeHandle, useLayoutEffect, useRef } from 'react'
import { UserLocationDot } from '@metro/route-card'
import { canvasToScreen } from '../interaction/canvasToScreen'
import styles from './UserLocationDotOverlay.module.css'

/** Marker footprint (the green 3-ring `UserLocationDot` is 35×35, spec
 *  §1.5) — used to centre the overlay on the station's screen point. */
const DOT_SIZE = 35

/**
 * Imperative handle exposed to {@link MetroMapCanvas} — the dot's CSS
 * position is updated from the same rAF tick that redraws the canvas,
 * so panning never triggers a React re-render (mirrors
 * {@link BubbleOverlay}).
 */
export interface UserLocationDotOverlayHandle {
  apply(zoom: number, panX: number, panY: number, dpr: number): void
}

export interface UserLocationDotOverlayProps {
  /**
   * Canvas position of the station nearest the user, or `null` when the
   * dot should be hidden (no fix, outside the UK, or beyond the
   * distance threshold — see `userLocationDot.isUserLocationDotVisible`).
   */
  readonly target: { readonly canvasX: number; readonly canvasY: number } | null
  /**
   * Current map transform getter — used to position the dot before
   * first paint so it does not flash at the viewport origin. Must be a
   * stable reference (it is read during layout).
   */
  readonly getTransform: () => { zoom: number; panX: number; panY: number; dpr: number }
}

/**
 * TASK-391 §3.6 — placeholder user-location dot drawn on the metro map
 * at the station nearest the user. The dot re-snaps live as the user
 * moves (the host feeds a new `target` from the `Location` store
 * stream). Visual design is refined separately (spec §7).
 */
export const UserLocationDotOverlay = forwardRef<
  UserLocationDotOverlayHandle,
  UserLocationDotOverlayProps
>(function UserLocationDotOverlay({ target, getTransform }, ref) {
  const dotRef = useRef<HTMLDivElement>(null)

  function position(zoom: number, panX: number, panY: number, dpr: number): void {
    const el = dotRef.current
    if (el === null || target === null || zoom <= 0) return
    const { x, y } = canvasToScreen(target.canvasX, target.canvasY, panX, panY, zoom)
    // translate3d instead of left/top: compositor-only, no layout
    // invalidation per frame (Task 1.10). The .dot class anchors the
    // element at left/top 0, so the translate equals the old left/top
    // values 1:1.
    el.style.transform = `translate3d(${x / dpr - DOT_SIZE / 2}px, ${y / dpr - DOT_SIZE / 2}px, 0)`
  }

  useImperativeHandle(
    ref,
    () => ({
      apply: (zoom, panX, panY, dpr) => position(zoom, panX, panY, dpr),
    }),
    // `target` is closed over by `position`; re-create the handle when
    // it changes so the host always calls a fresh closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [target],
  )

  // Position before first paint so a freshly-shown dot does not flash
  // at (0, 0) for one frame.
  useLayoutEffect(() => {
    if (target === null) return
    const t = getTransform()
    position(t.zoom, t.panX, t.panY, t.dpr)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, getTransform])

  if (target === null) return null
  // Green breathing 3-ring marker (Figma spec §1.5), painted at the
  // station's screen point and drawn UNDER the A/B route bubbles (the
  // bubble overlay host sits at a higher z-index — see the .dot z-index).
  // The host wrapper owns positioning; `UserLocationDot` owns the pulse.
  return (
    <div ref={dotRef} className={styles.dot} data-testid="user-location-dot-anchor">
      <UserLocationDot />
    </div>
  )
})
